// «الصيانة» — one request / work order (GET /maintenance/items/{type}/{id}): fields, timeline,
// root cause, parts and cost, technician, before / after photos and the deep link to the
// maintenance system. Photos load when the sheet opens, from GET .../photos (PHOTOS v1 through
// ops): short-lived presigned URLs, shown as plain lazy <img> with a fallback when one fails;
// when the feed is off or the maintenance system cannot answer, «الصور غير متاحة».
import { useEffect, useState } from "react";
import { ExternalLink, ImageOff, Repeat } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiErrorText } from "@/lib/errorText";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { InlineLoader } from "@/components/common/Loader";
import { MAINT } from "@/constants/testIds";
import {
    critLabel, critTone, fmtDuration, fmtSar, fmtTemplate, fmtWhen, itemBranchName, itemLocationName, partName, partQty,
    partUnit, rootCauseLabel, statusLabel, statusTone,
} from "./maintText";

const PHOTO_KINDS = { wr: ["request"], wo: ["before", "after"] };

const head = "text-xs font-bold text-[color:var(--ink-muted)]";

function Photo({ src, t }) {
    const [broken, setBroken] = useState(false);
    if (broken) {
        return (
            <span data-testid={MAINT.itemPhotoBroken}
                  className="flex aspect-square items-center justify-center gap-1 rounded-[var(--radius-md)] border border-dashed border-[color:var(--border-soft)] p-2 text-center text-[10px] font-semibold text-[color:var(--ink-muted)]">
                <ImageOff className="h-4 w-4 shrink-0" /><span>{t("mt_photo_broken")}</span>
            </span>
        );
    }
    return (
        <a href={src} target="_blank" rel="noopener noreferrer" className="block">
            <img data-testid={MAINT.itemPhoto} src={src} alt="" loading="lazy" decoding="async" referrerPolicy="no-referrer"
                 onError={() => setBroken(true)}
                 className="aspect-square w-full rounded-[var(--radius-md)] bg-[color:var(--bg-surface-2)] object-cover" />
        </a>
    );
}

function Photos({ id, title, urls, t }) {
    return (
        <div data-testid={MAINT.itemPhotos(id)} className="space-y-1.5">
            {title ? <p className="text-[11px] font-bold text-[color:var(--ink-faint)]">{title}</p> : null}
            {urls?.length ? (
                <div className="grid grid-cols-3 gap-2">
                    {urls.map((u) => <Photo key={u} src={u} t={t} />)}
                </div>
            ) : <p className="text-xs text-[color:var(--ink-muted)]">{t("mt_photos_none")}</p>}
        </div>
    );
}

/** `ph` = null while loading, {state: "ok", photos, unavailable} or {state: "off"}. */
function PhotosSection({ type, ph, it, t }) {
    const kinds = PHOTO_KINDS[type] || [];
    return (
        <section data-testid={MAINT.itemPhotosSection} data-state={ph ? ph.state : "loading"} className="space-y-2">
            <h4 className={head}>{t("mt_photos")}</h4>
            {!ph ? (
                <div className="space-y-1.5" aria-busy="true">
                    <span className="sr-only">{t("mt_photos_loading")}</span>
                    <div className="grid grid-cols-3 gap-2">
                        {[0, 1, 2].map((i) => <Skeleton key={i} className="aspect-square w-full" />)}
                    </div>
                </div>
            ) : ph.state === "off" ? (
                <p data-testid={MAINT.itemPhotosUnavailable}
                   className="flex items-center gap-1.5 rounded-[var(--radius-md)] border border-dashed border-[color:var(--border-soft)] px-3 py-2 text-xs font-semibold text-[color:var(--ink-muted)]">
                    <ImageOff className="h-4 w-4 shrink-0" /><span>{t("mt_photos_unavailable")}</span>
                </p>
            ) : (
                <>
                    {kinds.map((k) => (
                        <Photos key={k} id={k} title={type === "wo" ? t(`mt_photos_${k}`) : null} t={t}
                                urls={ph.photos.filter((x) => x.kind === k).map((x) => x.url)} />
                    ))}
                    {ph.unavailable ? (
                        <p data-testid={MAINT.itemPhotosSomeUnavailable} className="flex items-center gap-1.5 text-[11px] font-semibold text-[color:var(--ink-muted)]">
                            <ImageOff className="h-3.5 w-3.5 shrink-0" /><span>{fmtTemplate(t("mt_photos_some_unavailable"), { n: ph.unavailable })}</span>
                        </p>
                    ) : null}
                </>
            )}
            {it?.has_voice_note ? (
                <p className="text-[11px] font-semibold text-[color:var(--ink-muted)]">
                    <span>{t("mt_voice_note_hint")}</span>
                </p>
            ) : null}
            {it?.maint_url ? (
                <a data-testid={MAINT.itemPhotosOpenLink} href={it.maint_url} target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-1 text-xs font-bold text-[color:var(--brand-brown)] underline-offset-2 hover:underline">
                    <ExternalLink className="h-3.5 w-3.5 shrink-0" /><span>{t("mt_open_in_maint")}</span>
                </a>
            ) : null}
        </section>
    );
}

function Fields({ it, t, lang }) {
    const yesNo = (v) => (v === true ? t("mt_yes") : v === false ? t("mt_no") : t("mt_not_recorded"));
    const rows = [
        ["location", itemLocationName(it, lang)],
        ["branch", itemBranchName(it, lang)],
        ["sub_unit", it.sub_unit_code, true],
        ["asset", it.asset_code, true],
        ["created_at", fmtWhen(it.created_at, lang)],
        ["created_by", it.created_by_name],
        ["technician", it.technician ? it.technician.name || it.technician.id : null],
        ["sla", it.sla_target_minutes ? fmtDuration(it.sla_target_minutes, t) : null],
        ["ttc", it.time_to_complete_minutes !== undefined && it.time_to_complete_minutes !== null ? fmtDuration(it.time_to_complete_minutes, t) : null],
        ["actual", it.actual_minutes ? fmtDuration(it.actual_minutes, t) : null],
        ["sla_met", it.type === "wo" && (it.completed_at || it.sla_met !== null)
            ? `${yesNo(it.sla_met)}${it.sla_basis ? ` (${t(`mt_basis_${it.sla_basis}`)})` : ""}` : null],
        ["branch_confirmed", it.type === "wo" && (it.completed_at || it.closed_at)
            ? `${yesNo(it.branch_confirmed)}${it.branch_confirmed_at ? ` · ${fmtWhen(it.branch_confirmed_at, lang)}` : ""}` : null],
    ].filter(([, v]) => v !== null && v !== undefined && v !== "");
    return (
        <dl className="grid grid-cols-2 gap-x-3 gap-y-2.5">
            {rows.map(([k, v, ltr]) => (
                <div key={k} data-testid={MAINT.itemField(k)} className="min-w-0">
                    <dt className="text-[11px] text-[color:var(--ink-faint)]">{t(`mt_fld_${k}`)}</dt>
                    <dd className="break-words text-sm font-semibold text-[color:var(--ink)]">{ltr ? <span dir="ltr">{v}</span> : <bdi>{v}</bdi>}</dd>
                </div>
            ))}
        </dl>
    );
}

function Timeline({ events, t, lang }) {
    if (!events?.length) return null;
    return (
        <section data-testid={MAINT.itemTimeline} className="space-y-2">
            <h4 className={head}>{t("mt_timeline")}</h4>
            <ol className="space-y-2 border-s-2 border-[color:var(--border-soft)] ps-3">
                {events.map((e, i) => (
                    <li key={`${e.key}-${i}`} data-key={e.key} className="relative">
                        <span className="absolute -start-[19px] top-1.5 h-2.5 w-2.5 rounded-full bg-[color:var(--brand-orange)]" />
                        <p className="text-sm font-semibold text-[color:var(--ink)]">
                            <span>{t(`mt_ev_${e.key}`)}</span>
                            {e.to ? <span> · <span>{t("mt_ev_to")}</span> <bdi>{e.to}</bdi></span> : null}
                            {e.by ? <span> · <span>{t("mt_ev_by")}</span> <bdi>{e.by}</bdi></span> : null}
                        </p>
                        {e.at ? <p className="text-[11px] text-[color:var(--ink-faint)]">{fmtWhen(e.at, lang)}</p> : null}
                        {e.note ? <p className="text-xs text-[color:var(--ink-muted)]"><bdi>{e.note}</bdi></p> : null}
                    </li>
                ))}
            </ol>
        </section>
    );
}

function Parts({ it, t, lang }) {
    const parts = it.parts || [];
    return (
        <section data-testid={MAINT.itemParts} className="space-y-2">
            <h4 className={head}>{t("mt_parts")}</h4>
            {!parts.length ? <p className="text-xs text-[color:var(--ink-muted)]">{t("mt_parts_none")}</p> : (
                <div className="overflow-x-auto rounded-[var(--radius-md)] border border-[color:var(--border-soft)]">
                    <table className="w-full text-xs">
                        <thead className="bg-[color:var(--bg-surface-2)] text-[color:var(--ink-muted)]">
                            <tr>
                                <th className="px-2 py-1.5 text-start font-bold">{t("mt_part_name")}</th>
                                <th className="px-2 py-1.5 text-end font-bold">{t("mt_part_qty")}</th>
                                <th className="px-2 py-1.5 text-end font-bold">{t("mt_part_unit")}</th>
                                <th className="px-2 py-1.5 text-end font-bold">{t("mt_part_total")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {parts.map((p, i) => (
                                <tr key={i} className="border-t border-[color:var(--separator)]">
                                    <td className="px-2 py-1.5 text-[color:var(--ink)]"><bdi>{partName(p)}</bdi></td>
                                    <td className="px-2 py-1.5 text-end tnum">{partQty(p) ?? "—"}</td>
                                    <td className="px-2 py-1.5 text-end tnum">{partUnit(p) === null ? "—" : fmtSar(Number(partUnit(p)), lang, t)}</td>
                                    <td className="px-2 py-1.5 text-end font-semibold tnum">{fmtSar(p.line_total, lang, t)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {it.parts_total !== null && it.parts_total !== undefined ? (
                <p className="flex items-center justify-between text-sm font-extrabold text-[color:var(--ink)]">
                    <span>{t("mt_parts_total")}</span><span className="tnum">{fmtSar(it.parts_total, lang, t)}</span>
                </p>
            ) : null}
        </section>
    );
}

function Linked({ label, brief, t, onOpen }) {
    if (!brief) return null;
    return (
        <button type="button" onClick={() => onOpen(brief)}
                className="flex w-full items-center justify-between gap-2 rounded-[var(--radius-md)] border border-[color:var(--border-soft)] px-3 py-2 text-start text-xs transition-[background-color] duration-150 hover:bg-[color:var(--brand-orange-soft)]">
            <span className="min-w-0">
                <span className="block font-bold text-[color:var(--ink-muted)]">{label}</span>
                <span className="block truncate text-sm font-semibold text-[color:var(--ink)]"><span dir="ltr">{brief.code}</span> · <bdi>{brief.title}</bdi></span>
            </span>
            <Badge variant={statusTone(brief.status)} className="shrink-0 px-2 py-0 text-[10px]"><span>{statusLabel(t, brief.status)}</span></Badge>
        </button>
    );
}

/** `target` = {type, id} or null. */
export function ItemSheet({ target, onClose, onOpen, t, lang }) {
    const [it, setIt] = useState(null);
    const [ph, setPh] = useState(null);
    useEffect(() => {
        setIt(null);
        setPh(null);
        if (!target) return undefined;
        let alive = true;
        const path = `/maintenance/items/${target.type}/${encodeURIComponent(target.id)}`;
        api.get(path)
            .then((r) => { if (alive) setIt(r.data); })
            .catch((e) => { if (alive) { toast.error(apiErrorText(e)); onClose(); } });
        // the photos only when the sheet opens: one signed request to the maintenance system;
        // their URLs live minutes and are never kept beyond this sheet
        api.get(`${path}/photos`)
            .then((r) => {
                if (!alive) return;
                const d = r.data || {};
                setPh(d.available === false || !Array.isArray(d.photos) ? { state: "off" }
                    : { state: "ok", photos: d.photos, unavailable: d.unavailable || 0 });
            })
            .catch(() => { if (alive) setPh({ state: "off" }); });
        return () => { alive = false; };
    }, [target?.type, target?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const rc = it?.root_cause;
    const rcText = rootCauseLabel(rc, lang) || it?.root_cause_id;
    return (
        <Sheet open={!!target} onOpenChange={(o) => { if (!o) onClose(); }}>
            {/* same placement as the other record panels (RecordSheet): the close button sits top-right */}
            <SheetContent side="right" data-testid={MAINT.itemSheet}
                          className="w-full overflow-y-auto bg-[color:var(--bg-surface)] p-0 sm:max-w-lg">
                <SheetHeader className="sticky top-0 z-10 border-b border-[color:var(--border-soft)] bg-[color:var(--bg-surface)] px-4 py-3 pr-12 text-start sm:text-start">
                    <SheetTitle className="text-base font-extrabold text-[color:var(--ink)]">
                        <span>{t(target?.type === "wr" ? "mt_item_wr" : "mt_item_wo")}</span>
                        {it?.code ? <span> · <span dir="ltr">{it.code}</span></span> : null}
                    </SheetTitle>
                    <SheetDescription className="sr-only">{t(target?.type === "wr" ? "mt_item_wr" : "mt_item_wo")}</SheetDescription>
                </SheetHeader>
                {!it ? <InlineLoader /> : (
                    <div className="space-y-5 p-4">
                        <div className="space-y-2">
                            <h3 className="text-lg font-extrabold leading-snug text-[color:var(--ink)]"><bdi>{it.title || "—"}</bdi></h3>
                            <div className="flex flex-wrap items-center gap-1.5">
                                <Badge data-testid={MAINT.itemField("status")} variant={statusTone(it.status)}><span>{statusLabel(t, it.status)}</span></Badge>
                                <Badge data-testid={MAINT.itemField("criticality")} variant={critTone(it.criticality)}><span>{critLabel(t, it.criticality)}</span></Badge>
                                {it.overdue ? <Badge variant="destructive"><span>{t("mt_kpi_overdue")}</span></Badge> : null}
                                {it.mapping_state === "suggested" ? <Badge variant="warning"><span>{t("mt_suggested")}</span></Badge> : null}
                            </div>
                            {it.repeat_of ? (
                                <p className="flex items-start gap-1.5 rounded-[var(--radius-md)] bg-[color:var(--brand-brown-soft)] px-3 py-2 text-xs font-semibold text-[color:var(--brand-brown-soft-fg)]">
                                    <Repeat className="mt-0.5 h-3.5 w-3.5 shrink-0" /><span>{t("mt_repeat_of")}</span>
                                </p>
                            ) : null}
                        </div>
                        {it.description ? (
                            <section className="space-y-1">
                                <h4 className={head}>{t("mt_desc")}</h4>
                                <p className="whitespace-pre-line text-sm text-[color:var(--ink)]"><bdi>{it.description}</bdi></p>
                            </section>
                        ) : null}
                        <Fields it={it} t={t} lang={lang} />
                        {it.type === "wo" ? (
                            <section data-testid={MAINT.itemField("root_cause")} className="space-y-1">
                                <h4 className={head}>{t("mt_root_cause")}</h4>
                                <p className="text-sm font-semibold text-[color:var(--ink)]"><bdi>{rcText || t("mt_not_recorded")}</bdi></p>
                                {it.root_cause_detail ? <p className="text-xs text-[color:var(--ink-muted)]"><bdi>{it.root_cause_detail}</bdi></p> : null}
                            </section>
                        ) : null}
                        {[["rejection", it.rejection_reason], ["close_reason", it.close_reason], ["reopen_reason", it.reopen_reason]]
                            .filter(([, v]) => v).map(([k, v]) => (
                                <section key={k} data-testid={MAINT.itemField(k)} className="space-y-1">
                                    <h4 className={head}>{t(`mt_${k}`)}</h4>
                                    <p className="text-sm text-[color:var(--ink)]"><bdi>{v}</bdi></p>
                                </section>
                            ))}
                        <Timeline events={it.timeline} t={t} lang={lang} />
                        {it.type === "wo" ? <Parts it={it} t={t} lang={lang} /> : null}
                        <PhotosSection type={it.type} ph={ph} it={it} t={t} />
                        <Linked label={t("mt_linked_wo")} brief={it.work_order} t={t} onOpen={onOpen} />
                        <Linked label={t("mt_linked_wr")} brief={it.work_request} t={t} onOpen={onOpen} />
                        {it.maint_url ? (
                            <a data-testid={MAINT.itemOpenLink} href={it.maint_url} target="_blank" rel="noopener noreferrer"
                               className="flex h-11 w-full items-center justify-center gap-2 rounded-full bg-[color:var(--brand-orange)] text-sm font-bold text-white transition-[background-color] duration-150 hover:bg-[color:var(--brand-orange-hover)]">
                                <ExternalLink className="h-4 w-4" /><span>{t("mt_open_in_maint")}</span>
                            </a>
                        ) : null}
                        <button type="button" data-testid={MAINT.itemClose} onClick={onClose}
                                className="h-11 w-full rounded-full border border-[color:var(--border-soft)] text-sm font-bold text-[color:var(--ink)] transition-[background-color] duration-150 hover:bg-[color:var(--bg-surface-2)]">
                            {t("mt_close")}
                        </button>
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
}
